<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useFunctionGetter } from 'dashboard/composables/store';
import foodicsAPI from 'dashboard/api/foodics';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const integration = useFunctionGetter('integrations/getIntegration', 'foodics');

const foodicsLogoUrl = '/dashboard/images/integrations/foodics.png';
const FOODICS_ORDER_PAGE_SIZE = 50;
const isLoading = ref(true);
const isWorking = ref(false);
const activeTab = ref('customers');
const status = ref({});
const search = ref('');

const resources = reactive({
  branches: { page: 1, payload: null, loading: false },
  customers: { page: 1, payload: null, loading: false },
  orders: { page: 1, payload: null, loading: false, referenceAfter: '0', history: [] },
});

const tabs = computed(() => [
  { key: 'customers', label: t('INTEGRATION_SETTINGS.FOODICS.TABS.CUSTOMERS') },
  { key: 'orders', label: t('INTEGRATION_SETTINGS.FOODICS.TABS.ORDERS') },
  { key: 'branches', label: t('INTEGRATION_SETTINGS.FOODICS.TABS.BRANCHES') },
]);

const connected = computed(() => !!status.value.connected);
const configured = computed(() => !!status.value.configured);
const currentResource = computed(() => resources[activeTab.value]);
const currentItems = computed(() => currentResource.value.payload?.data || []);
const currentMeta = computed(() => currentResource.value.payload?.meta || {});
const hasNextPage = computed(() => {
  if (activeTab.value === 'orders') {
    return currentItems.value.length >= FOODICS_ORDER_PAGE_SIZE;
  }

  const meta = currentMeta.value;
  if (meta.last_page) return Number(meta.current_page || currentResource.value.page) < Number(meta.last_page);
  return currentItems.value.length > 0;
});

const businessName = computed(() => {
  const business = status.value.business;
  if (!business) return '';
  if (typeof business === 'string') return business;
  return business.name || business.reference || '';
});

const normalizePhone = customer => {
  const phone = customer.phone || customer.phone_number || customer.mobile || '';
  if (!phone) return '';
  if (phone.startsWith('+')) return phone;
  return `${customer.dial_code ? `+${customer.dial_code}` : ''}${phone}`;
};

const displayedRecord = resourceRecord => {
  return activeTab.value === 'orders' ? resourceRecord.order || resourceRecord : resourceRecord;
};

const resourceRowKey = (resourceRecord, rowIndex) => {
  const record = displayedRecord(resourceRecord);
  return record.id || record.reference || record.number || `${activeTab.value}-${currentResource.value.page}-${rowIndex}`;
};

const resourceRowTitle = resourceRecord => {
  const record = displayedRecord(resourceRecord);
  return record.name || record.reference || record.number || record.id || '-';
};

const resourceRowDetail = resourceRecord => {
  const record = displayedRecord(resourceRecord);
  if (activeTab.value === 'customers') return record.email || normalizePhone(record) || '-';
  if (activeTab.value === 'orders') return record.reference || record.number || record.type || '-';
  return record.phone || record.address || record.reference || '-';
};

const resourceRowStatus = resourceRecord => {
  const record = displayedRecord(resourceRecord);
  return record.status || record.active || record.deleted_at || '-';
};

const orderReferenceValue = resourceRecord => {
  const reference = displayedRecord(resourceRecord).reference;
  const numericReference = Number(reference);
  return Number.isFinite(numericReference) ? numericReference : null;
};

const lastOrderReference = () => {
  const references = currentItems.value
    .map(orderReferenceValue)
    .filter(reference => reference !== null);

  if (!references.length) return null;
  return String(Math.max(...references));
};

const loadStatus = async () => {
  const { data } = await foodicsAPI.status();
  status.value = data;
};

const redirectIfServiceDisabled = () => {
  if (status.value.tenant_enabled !== false) return false;

  router.replace({ path: route.path.replace(/\/[^/]+\/?$/, '') });
  return true;
};

const loadResource = async (type = activeTab.value) => {
  if (!connected.value) return;
  const resource = resources[type];
  resource.loading = true;
  try {
    const requestMap = {
      branches: () => foodicsAPI.branches({ page: resource.page }),
      customers: () =>
        foodicsAPI.customers({ page: resource.page, search: search.value }),
      orders: () => foodicsAPI.orders({ referenceAfter: resource.referenceAfter }),
    };
    const { data } = await requestMap[type]();
    resource.payload = data;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.FOODICS.API.ERROR'));
  } finally {
    resource.loading = false;
  }
};

const connectFoodics = async () => {
  isWorking.value = true;
  try {
    const { data } = await foodicsAPI.authUrl();
    window.location.href = data.url;
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.FOODICS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const disconnectFoodics = async () => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.FOODICS.DISCONNECT_CONFIRM'))) return;

  isWorking.value = true;
  try {
    await foodicsAPI.disconnect();
    await store.dispatch('integrations/get');
    await loadStatus();
    useAlert(t('INTEGRATION_SETTINGS.FOODICS.API.DISCONNECTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.FOODICS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const importCustomer = async customer => {
  isWorking.value = true;
  try {
    await foodicsAPI.importCustomer(customer);
    useAlert(t('INTEGRATION_SETTINGS.FOODICS.API.CUSTOMER_IMPORTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('INTEGRATION_SETTINGS.FOODICS.API.ERROR'));
  } finally {
    isWorking.value = false;
  }
};

const setOrderPage = async direction => {
  const resource = resources.orders;
  if (direction > 0) {
    const nextReference = lastOrderReference();
    if (!nextReference) return;

    resource.history.push(resource.referenceAfter);
    resource.referenceAfter = nextReference;
    resource.page += 1;
  } else {
    const previousReference = resource.history.pop();
    if (previousReference === undefined) return;

    resource.referenceAfter = previousReference;
    resource.page -= 1;
  }

  await loadResource('orders');
};

const setPage = async direction => {
  if (activeTab.value === 'orders') {
    await setOrderPage(direction);
    return;
  }

  const resource = currentResource.value;
  const nextPage = resource.page + direction;
  if (nextPage < 1) return;
  resource.page = nextPage;
  await loadResource();
};

const refreshCustomers = async () => {
  resources.customers.page = 1;
  await loadResource('customers');
};

const initialize = async () => {
  try {
    await store.dispatch('integrations/get');
    await loadStatus();
    if (redirectIfServiceDisabled()) return;
    if (route.query.connected) {
      useAlert(t('INTEGRATION_SETTINGS.FOODICS.API.CONNECTED'));
    }
    if (route.query.error) {
      useAlert(t('INTEGRATION_SETTINGS.FOODICS.API.ERROR'));
    }
    if (connected.value) await loadResource(activeTab.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.FOODICS.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.FOODICS.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5">
        <section
          class="flex flex-col gap-4 rounded-lg outline outline-1 outline-n-container bg-n-card p-5 md:flex-row md:items-center md:justify-between"
        >
          <div class="flex gap-4">
          <img
            :src="foodicsLogoUrl"
            class="h-14 w-14 rounded-lg border border-n-weak bg-n-alpha-2"
            alt="Foodics"
          />
            <div>
              <h3 class="text-heading-2 text-n-slate-12">
                {{ integration.name || 'Foodics' }}
              </h3>
              <p class="mt-1 text-body-main text-n-slate-11">
                {{ integration.description || $t('INTEGRATION_SETTINGS.FOODICS.DESCRIPTION') }}
              </p>
              <p v-if="businessName" class="mt-2 text-sm text-n-teal-11">
                {{ $t('INTEGRATION_SETTINGS.FOODICS.CONNECTED_TO', { business: businessName }) }}
              </p>
              <p v-else-if="!configured" class="mt-2 text-sm text-n-ruby-11">
                {{ $t('INTEGRATION_SETTINGS.FOODICS.NOT_CONFIGURED') }}
              </p>
            </div>
          </div>
          <div class="flex gap-2">
            <Button
              v-if="connected"
              faded
              ruby
              :is-loading="isWorking"
              :label="$t('INTEGRATION_SETTINGS.FOODICS.DISCONNECT')"
              @click="disconnectFoodics"
            />
            <Button
              v-else
              :is-loading="isWorking"
              :disabled="!configured"
              :label="$t('INTEGRATION_SETTINGS.FOODICS.CONNECT')"
              @click="connectFoodics"
            />
          </div>
        </section>

        <section
          v-if="connected"
          class="rounded-lg outline outline-1 outline-n-container bg-n-card"
        >
          <div class="flex flex-wrap gap-2 border-b border-n-weak p-3">
            <Button
              v-for="tab in tabs"
              :key="tab.key"
              size="sm"
              :variant="activeTab === tab.key ? 'solid' : 'faded'"
              color="slate"
              :label="tab.label"
              @click="
                activeTab = tab.key;
                loadResource(tab.key);
              "
            />
          </div>

          <div v-if="activeTab === 'customers'" class="flex gap-2 p-4">
            <Input
              v-model="search"
              :placeholder="$t('INTEGRATION_SETTINGS.FOODICS.SEARCH_CUSTOMERS')"
              class="max-w-md"
              @keyup.enter="refreshCustomers"
            />
            <Button
              faded
              slate
              :label="$t('INTEGRATION_SETTINGS.FOODICS.SEARCH')"
              @click="refreshCustomers"
            />
          </div>

          <div class="overflow-x-auto p-4 pt-0">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.FOODICS.TABLE.NAME') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.FOODICS.TABLE.DETAIL') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.FOODICS.TABLE.STATUS') }}</th>
                  <th class="px-3 py-2">{{ $t('INTEGRATION_SETTINGS.FOODICS.TABLE.ACTIONS') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak text-n-slate-12">
                <tr v-if="currentResource.loading">
                  <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">
                    {{ $t('INTEGRATION_SETTINGS.FOODICS.LOADING') }}
                  </td>
                </tr>
                <template v-else>
                  <tr
                    v-for="(resourceRecord, rowIndex) in currentItems"
                    :key="resourceRowKey(resourceRecord, rowIndex)"
                  >
                    <td class="px-3 py-3">
                      {{ resourceRowTitle(resourceRecord) }}
                    </td>
                    <td class="px-3 py-3 text-n-slate-11">
                      {{ resourceRowDetail(resourceRecord) }}
                    </td>
                    <td class="px-3 py-3 text-n-slate-11">
                      {{ resourceRowStatus(resourceRecord) }}
                    </td>
                    <td class="px-3 py-3">
                      <Button
                        v-if="activeTab === 'customers'"
                        xs
                        faded
                        blue
                        :is-loading="isWorking"
                        :label="$t('INTEGRATION_SETTINGS.FOODICS.IMPORT_CUSTOMER')"
                        @click="importCustomer(resourceRecord)"
                      />
                    </td>
                  </tr>
                </template>
                <tr v-if="!currentResource.loading && !currentItems.length">
                  <td colspan="4" class="px-3 py-8 text-center text-n-slate-11">
                    {{ $t('INTEGRATION_SETTINGS.FOODICS.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="flex items-center justify-between border-t border-n-weak p-4">
            <span class="text-sm text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.FOODICS.PAGE', { page: currentResource.page }) }}
            </span>
            <div class="flex gap-2">
              <Button
                xs
                faded
                slate
                :disabled="currentResource.page <= 1 || currentResource.loading"
                :label="$t('INTEGRATION_SETTINGS.FOODICS.PREVIOUS')"
                @click="setPage(-1)"
              />
              <Button
                xs
                faded
                slate
                :disabled="!hasNextPage || currentResource.loading"
                :label="$t('INTEGRATION_SETTINGS.FOODICS.NEXT')"
                @click="setPage(1)"
              />
            </div>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
